import {
  Album,
  AlbumMemoryVideo,
  CreateAlbumDto,
  CreateAlbumMemoryVideoDto,
  UpdateAlbumDto,
  AlbumLink,
  CreateAlbumLinkDto,
  AlbumComment,
  CreateAlbumCommentDto,
  UpdateAlbumCommentDto,
  AlbumLike,
  AlbumShare,
  AlbumSharePreview,
} from "../types/album";
import { makeAuthenticatedRequest, makeAuthenticatedRequestRaw } from "./tokenHelper";

class AlbumService {
  async getUserAlbums(ownerId: string, offset: number = 0, limit: number = 20): Promise<Album[]> {
    return makeAuthenticatedRequest<Album[]>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums?offset=${offset}&limit=${limit}`
    );
  }

  async getAlbum(ownerId: string, albumId: string): Promise<Album> {
    return makeAuthenticatedRequest<Album>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}`
    );
  }

  async markAlbumAsAccessed(ownerId: string, albumId: string): Promise<void> {
    return makeAuthenticatedRequest<void>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/access`,
      {
        method: "POST",
      }
    );
  }

  async createAlbum(ownerId: string, albumData: CreateAlbumDto): Promise<Album> {
    return makeAuthenticatedRequest<Album>(`/api/v1/users/${encodeURIComponent(ownerId)}/albums`, {
      method: "POST",
      body: JSON.stringify(albumData),
    });
  }

  async updateAlbum(ownerId: string, albumId: string, updateData: UpdateAlbumDto): Promise<Album> {
    return makeAuthenticatedRequest<Album>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}`,
      {
        method: "PUT",
        body: JSON.stringify(updateData),
      }
    );
  }

  async deleteAlbum(ownerId: string, albumId: string): Promise<Album> {
    return makeAuthenticatedRequest<Album>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}`,
      {
        method: "DELETE",
      }
    );
  }

  async getAlbumLinks(ownerId: string, albumId: string, offset: number = 0, limit: number = 20): Promise<AlbumLink[]> {
    return makeAuthenticatedRequest<AlbumLink[]>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/links?offset=${offset}&limit=${limit}`
    );
  }

  async createAlbumLink(ownerId: string, albumId: string, linkData: CreateAlbumLinkDto): Promise<AlbumLink> {
    return makeAuthenticatedRequest<AlbumLink>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/links`,
      {
        method: "POST",
        body: JSON.stringify(linkData),
      }
    );
  }

  async deactivateAlbumLink(ownerId: string, albumId: string, linkId: string): Promise<AlbumLink> {
    return makeAuthenticatedRequest<AlbumLink>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/links/${encodeURIComponent(linkId)}`,
      {
        method: "DELETE",
      }
    );
  }

  async getAlbumComments(
    ownerId: string,
    albumId: string,
    offset: number = 0,
    limit: number = 10
  ): Promise<AlbumComment[]> {
    return makeAuthenticatedRequest<AlbumComment[]>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/comments?offset=${offset}&limit=${limit}`
    );
  }

  async createAlbumComment(
    ownerId: string,
    albumId: string,
    commentData: CreateAlbumCommentDto
  ): Promise<AlbumComment> {
    return makeAuthenticatedRequest<AlbumComment>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/comments`,
      {
        method: "POST",
        body: JSON.stringify(commentData),
      }
    );
  }

  async updateAlbumComment(
    ownerId: string,
    albumId: string,
    commentId: string,
    updateData: UpdateAlbumCommentDto
  ): Promise<AlbumComment> {
    return makeAuthenticatedRequest<AlbumComment>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/comments/${encodeURIComponent(commentId)}`,
      {
        method: "PUT",
        body: JSON.stringify(updateData),
      }
    );
  }

  async deleteAlbumComment(ownerId: string, albumId: string, commentId: string): Promise<void> {
    return makeAuthenticatedRequest<void>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/comments/${encodeURIComponent(commentId)}`,
      {
        method: "DELETE",
      }
    );
  }

  async getAlbumLikes(ownerId: string, albumId: string, offset: number = 0, limit: number = 10): Promise<AlbumLike[]> {
    return makeAuthenticatedRequest<AlbumLike[]>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/likes?offset=${offset}&limit=${limit}`
    );
  }

  async createAlbumLike(ownerId: string, albumId: string): Promise<AlbumLike> {
    return makeAuthenticatedRequest<AlbumLike>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/likes`,
      {
        method: "POST",
      }
    );
  }

  async deleteAlbumLike(ownerId: string, albumId: string, likeId: string): Promise<void> {
    return makeAuthenticatedRequest<void>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/likes/${encodeURIComponent(likeId)}`,
      {
        method: "DELETE",
      }
    );
  }

  async previewAlbumShare(userId: string, token: string): Promise<AlbumSharePreview> {
    return makeAuthenticatedRequest<AlbumSharePreview>(
      `/api/v1/users/${encodeURIComponent(userId)}/shares/preview`,
      {
        method: "POST",
        body: JSON.stringify({ token }),
      }
    );
  }

  async createAlbumShare(ownerId: string, token: string): Promise<{ albumId: string; ownerId: string }> {
    const result = await makeAuthenticatedRequest<{ albumId: string; ownerId: string }>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/shares`,
      {
        method: "POST",
        body: JSON.stringify({ token }),
      }
    );

    // If this is an anonymous user, add the token to their share token list
    const { authService: auth } = await import("./authService");
    if (auth.isAnonymousUser()) {
      auth.addAnonymousShareToken(token);
    }

    return result;
  }

  async getSharedAlbums(userId: string): Promise<AlbumShare[]> {
    return makeAuthenticatedRequest<AlbumShare[]>(`/api/v1/users/${encodeURIComponent(userId)}/shares`);
  }

  async deleteAlbumShare(userId: string, shareId: string): Promise<void> {
    return makeAuthenticatedRequest<void>(
      `/api/v1/users/${encodeURIComponent(userId)}/shares/${encodeURIComponent(shareId)}`,
      {
        method: "DELETE",
      }
    );
  }

  async downloadAlbumZip(ownerId: string, albumId: string): Promise<void> {
    // Make the request and get the response
    const response = await makeAuthenticatedRequestRaw(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/zip`,
      {
        method: "GET",
      }
    );

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = "album.zip";
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-8'')?([^;\r\n"']*)['"]?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = decodeURIComponent(filenameMatch[1]);
      }
    }

    // Download the blob and trigger browser download
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  async downloadAlbumSelectionZip(
    ownerId: string,
    albumId: string,
    mediaIds: string[]
  ): Promise<void> {
    if (mediaIds.length === 0) return;

    const response = await makeAuthenticatedRequestRaw(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/zip`,
      {
        method: "POST",
        body: JSON.stringify({ mediaIds }),
      }
    );

    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = "album-selection.zip";
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-8'')?([^;\r\n"']*)['"]?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = decodeURIComponent(filenameMatch[1]);
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  async getAlbumMemoryVideo(ownerId: string, albumId: string): Promise<AlbumMemoryVideo | null> {
    return makeAuthenticatedRequest<AlbumMemoryVideo | null>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/memory-video`
    );
  }

  async createAlbumMemoryVideo(
    ownerId: string,
    albumId: string,
    payload: CreateAlbumMemoryVideoDto = {}
  ): Promise<AlbumMemoryVideo> {
    return makeAuthenticatedRequest<AlbumMemoryVideo>(
      `/api/v1/users/${encodeURIComponent(ownerId)}/albums/${encodeURIComponent(albumId)}/memory-video`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
  }
}

export const albumService = new AlbumService();
